import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { packPages } from "./pack-pages";
import type { PageRange } from "./pack-pages";
import styles from "./paging.module.css";

/**
 * **Pages that fit the window, never a scroll bar** (decision PF4, 2026-09-27).
 *
 * The client's words: *"apply pagination across the entire patient tabs this way
 * I don't have endless scrolls, I don't like scrolls at all"* — and on a
 * 1366 × 768 laptop too, *"make the pages a few rows shorter"*. So a page is not
 * a fixed number of rows: it is **however many whole items fit the height the
 * list actually has**, and it is recomputed when the window changes.
 *
 * ── How it knows what fits ──────────────────────────────────────────────────
 *
 * Each item's height is **measured once it has been drawn**, and estimated until
 * then. Rows are fixed-height in CSS, so the estimate is exact for them; the
 * measurement matters for the things that vary — an open note, an invoice with
 * its payment form showing — and those are always on the page being looked at,
 * so they are measured the moment they change. An item never splits across two
 * pages: a day, a treatment and an invoice each stay whole.
 *
 * **The list area keeps `overflow-y: auto` as a last resort.** A single item
 * taller than the whole window — a tooth with a very long history on a phone —
 * scrolls inside the list rather than being clipped. On a desktop window that
 * does not happen; the prototype measured every page of every tab at 1440 × 900
 * and 1366 × 768.
 */

export { packPages } from "./pack-pages";
export type { PageRange } from "./pack-pages";

/**
 * The height an element has, kept current as the window and the layout change.
 *
 * The observer lives and dies with the element, through the cleanup a React 19
 * callback ref may return — not in an effect, whose cleanup StrictMode runs on
 * mount and which would disconnect it for good.
 */
export function useHeight<T extends HTMLElement>(): [
  (element: T | null) => (() => void) | undefined,
  number,
] {
  const [height, setHeight] = useState(0);

  const ref = useCallback((element: T | null) => {
    if (element === null) return undefined;
    setHeight(element.clientHeight);
    const observer = new ResizeObserver(() => setHeight(element.clientHeight));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, height];
}

/** `useHeight`, with the width beside it. */
export function useSize<T extends HTMLElement>(): [
  (element: T | null) => (() => void) | undefined,
  { width: number; height: number },
] {
  const [size, setSize] = useState({ width: 0, height: 0 });

  const ref = useCallback((element: T | null) => {
    if (element === null) return undefined;
    const read = (): void =>
      setSize((was) =>
        was.width === element.clientWidth && was.height === element.clientHeight
          ? was
          : { width: element.clientWidth, height: element.clientHeight },
      );
    read();
    const observer = new ResizeObserver(read);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, size];
}

/**
 * The height from an element's top to the bottom of the content area (the
 * shell's `<main>`, less its padding), less `reserve` — kept current as the
 * window, the layout and the element itself change. Never below one entry's
 * worth, so a short window still shows something on each page.
 *
 * **For a list in a card that grows with what it holds** — the calendar's side
 * panel (Emile, 2026-10-17: *"please always calculate to fit screen size … for
 * any screen size"*), where measuring the list would be measuring its rows.
 * Pass the result to `useFitPages` as its `budget`.
 */
export function useRoomBelow(reserve: number): [(element: HTMLDivElement | null) => void, number] {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [room, setRoom] = useState(0);

  useLayoutEffect(() => {
    if (element === null) return undefined;
    const main = element.closest("main");
    const read = (): void => {
      // Hidden (the side panel shows the waitlist): nothing to measure yet.
      if (main === null || element.offsetParent === null) return;
      const bottom =
        main.getBoundingClientRect().bottom - parseFloat(getComputedStyle(main).paddingBottom);
      setRoom(Math.max(130, Math.floor(bottom - element.getBoundingClientRect().top - reserve)));
    };
    read();
    const observer = new ResizeObserver(read);
    observer.observe(element);
    // Something opening above the list (a form, an error) moves its top.
    if (element.parentElement !== null) observer.observe(element.parentElement);
    if (main !== null) observer.observe(main);
    window.addEventListener("resize", read);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", read);
    };
  }, [element, reserve]);

  return [setElement, room];
}

/** Which item to keep on screen — see `useFitPages`. */
export interface Follow {
  key: string;
}

export interface FitPages {
  /** The list area: its height is the page's budget. */
  areaRef: (element: HTMLDivElement | null) => (() => void) | undefined;
  /** Put on each drawn item so its real height replaces the estimate. */
  measure: (key: string) => (element: HTMLElement | null) => (() => void) | undefined;
  page: number;
  setPage: (page: number) => void;
  pages: PageRange[];
  /** The items on the current page, `[start, end)`. */
  range: PageRange;
}

/**
 * Pages of whole items that fit the list area's height.
 *
 * `keys` identifies the items in order; `estimate` is used for any not yet
 * measured; `gap` is the space the list draws between items. The page goes back
 * to the first whenever `resetOn` changes — another patient, another search.
 */
export function useFitPages({
  keys,
  estimate,
  gap,
  resetOn,
  follow,
  budget: fixedBudget,
}: {
  keys: readonly string[];
  estimate: (index: number) => number;
  gap: number;
  resetOn?: unknown;
  /**
   * The item somebody is working on. Opening a note or an invoice makes it
   * taller, and a taller item can be packed onto the next page — so the pager
   * follows it there rather than let it vanish from under the click. A fresh
   * object per interaction; turning the page by hand lets go of it.
   */
  follow?: Follow | null;
  /**
   * A height worked out by the caller, in place of the list area's own. For a
   * list whose card grows with its rows up to a limit — Staff's waiting
   * requests (ST16) — where measuring the area would measure the rows.
   */
  budget?: number;
}): FitPages {
  const [areaRef, measuredBudget] = useHeight<HTMLDivElement>();
  const budget = fixedBudget ?? measuredBudget;
  const measured = useRef(new Map<string, number>());
  const [version, setVersion] = useState(0);
  const [page, setPage] = useState(0);

  const keyOf = useRef(new WeakMap<Element, string>());
  const callbacks = useRef(
    new Map<string, (element: HTMLElement | null) => (() => void) | undefined>(),
  );

  /**
   * One observer for every item, made on first use. **Not in an effect**: item
   * refs attach before effects run, so an observer made there did not exist yet
   * when the first page's items asked to be watched — they were never measured.
   * Each item unobserves itself when it goes, through its ref's cleanup.
   */
  const observer = useRef<ResizeObserver | null>(null);
  const observerFor = useCallback((): ResizeObserver => {
    observer.current ??= new ResizeObserver((entries) => {
      let changed = false;
      for (const entry of entries) {
        const key = keyOf.current.get(entry.target);
        if (key === undefined) continue;
        const height = (entry.target as HTMLElement).offsetHeight;
        if (height > 0 && measured.current.get(key) !== height) {
          measured.current.set(key, height);
          changed = true;
        }
      }
      if (changed) setVersion((value) => value + 1);
    });
    return observer.current;
  }, []);

  const measure = useCallback(
    (key: string) => {
      let callback = callbacks.current.get(key);
      if (callback === undefined) {
        callback = (element: HTMLElement | null) => {
          if (element === null) return undefined;
          keyOf.current.set(element, key);
          const watching = observerFor();
          watching.observe(element);
          return () => watching.unobserve(element);
        };
        callbacks.current.set(key, callback);
      }
      return callback;
    },
    [observerFor],
  );

  useEffect(() => {
    setPage(0);
  }, [resetOn]);

  // Packed every render: a few dozen additions, and what an estimate returns
  // changes with state the caller holds (an open note) as well as with `keys`.
  // `version` re-renders when a measurement lands.
  void version;
  const heights = keys.map((key, index) => measured.current.get(key) ?? estimate(index));
  // Before the area has a height (the first frame), everything is one page.
  const pages: PageRange[] =
    budget <= 0 ? [[0, keys.length] as const] : packPages(heights, budget, gap);

  const current = Math.min(page, pages.length - 1);

  /** The follow a page turn let go of, so the pager can leave that item's page. */
  const released = useRef<Follow | null>(null);

  useEffect(() => {
    if (follow === undefined || follow === null || released.current === follow) return;
    const index = keys.indexOf(follow.key);
    if (index < 0) return;
    const holding = pages.findIndex(([start, end]) => index >= start && index < end);
    if (holding >= 0 && holding !== current) setPage(holding);
    // Deliberately not on `current`: it runs when the packing or the item
    // changes, and a page turn by hand has released the item anyway.
  }, [pages, follow]);

  const turn = useCallback(
    (next: number) => {
      released.current = follow ?? null;
      setPage(next);
    },
    [follow],
  );

  return {
    areaRef,
    measure,
    page: current,
    setPage: turn,
    pages,
    range: pages[current] ?? [0, 0],
  };
}

/**
 * Numbered pages: `21–40 of 58 patients   ‹ 1 2 3 ›`.
 *
 * Draws nothing when everything fits on one page — a pager with one page is
 * a control that does nothing. With many pages it shows the first, the last and
 * the ones around the current, so the row never grows past a dozen buttons.
 */
export function Pager({
  page,
  count,
  onPage,
  from,
  to,
  total,
  noun,
  testId,
  compact,
}: {
  page: number;
  count: number;
  onPage: (page: number) => void;
  /** First and last item shown, 1-based, and how many in all. */
  from: number;
  to: number;
  total: number;
  noun: string;
  testId?: string;
  /**
   * `‹ 2 / 3 ›` and nothing else — for a list in a column too narrow for the
   * numbered row, such as the tooth editor's under the chart (PF13).
   */
  compact?: boolean;
}): React.JSX.Element | null {
  if (count <= 1) return null;

  if (compact === true) {
    return (
      <nav className={styles.compact} aria-label={`Pages of ${noun}`} data-testid={testId}>
        <button
          type="button"
          className={styles.step}
          disabled={page === 0}
          onClick={() => onPage(page - 1)}
          aria-label="Previous page"
          data-testid={testId === undefined ? undefined : `${testId}-prev`}
        >
          ‹
        </button>
        <span
          className={styles.compactInfo}
          aria-label={`${String(from)}–${String(to)} of ${String(total)} ${noun}`}
          data-testid={testId === undefined ? undefined : `${testId}-info`}
        >
          {page + 1} / {count}
        </span>
        <button
          type="button"
          className={styles.step}
          disabled={page >= count - 1}
          onClick={() => onPage(page + 1)}
          aria-label="Next page"
          data-testid={testId === undefined ? undefined : `${testId}-next`}
        >
          ›
        </button>
      </nav>
    );
  }

  const shown = pagesToShow(page, count);
  return (
    <nav className={styles.pager} aria-label={`Pages of ${noun}`} data-testid={testId}>
      <span
        className={styles.info}
        data-testid={testId === undefined ? undefined : `${testId}-info`}
      >
        {from}–{to} of {total} {noun}
      </span>
      <button
        type="button"
        className={styles.step}
        disabled={page === 0}
        onClick={() => onPage(page - 1)}
        aria-label="Previous page"
        data-testid={testId === undefined ? undefined : `${testId}-prev`}
      >
        ‹
      </button>
      {shown.map((entry, index) =>
        entry === null ? (
          <span key={`gap-${String(index)}`} className={styles.gap} aria-hidden="true">
            …
          </span>
        ) : (
          <button
            key={entry}
            type="button"
            className={entry === page ? styles.current : styles.number}
            aria-current={entry === page ? "page" : undefined}
            aria-label={`Page ${String(entry + 1)}`}
            onClick={() => onPage(entry)}
          >
            {entry + 1}
          </button>
        ),
      )}
      <button
        type="button"
        className={styles.step}
        disabled={page >= count - 1}
        onClick={() => onPage(page + 1)}
        aria-label="Next page"
        data-testid={testId === undefined ? undefined : `${testId}-next`}
      >
        ›
      </button>
    </nav>
  );
}

/** The page numbers worth a button, with `null` where a run is skipped. */
function pagesToShow(page: number, count: number): (number | null)[] {
  if (count <= 7) return Array.from({ length: count }, (_, index) => index);
  const wanted = new Set([0, count - 1, page - 1, page, page + 1]);
  const sorted = [...wanted].filter((n) => n >= 0 && n < count).sort((a, b) => a - b);
  const out: (number | null)[] = [];
  sorted.forEach((n, index) => {
    const previous = sorted[index - 1];
    if (previous !== undefined && n - previous > 1) out.push(null);
    out.push(n);
  });
  return out;
}

/**
 * **Rows of one height, paged to the box they are drawn in** (Emile, 2026-10-16:
 * *"apply pagination across the entire prototype where needed"*, on a busy
 * clinic). The cheap cousin of `useFitPages`, for lists and tables whose rows are
 * all the same height: the first drawn row is measured, and a page is however
 * many of it fit under the table's head.
 *
 * The box must have a definite height — `fitArea` below, inside a column that
 * reaches the content area's bottom.
 */
export function useRowPages({
  count,
  estimate = 44,
  resetOn,
}: {
  count: number;
  /** A row's height until the first one has been drawn. */
  estimate?: number;
  /** Back to the first page when this changes — a new search, another filter. */
  resetOn?: unknown;
}): {
  areaRef: (element: HTMLDivElement | null) => (() => void) | undefined;
  /** On the first row drawn, so its real height replaces the estimate. */
  rowRef: (element: HTMLElement | null) => void;
  /** On a table's head, so its height comes off the budget. */
  headRef: (element: HTMLElement | null) => void;
  start: number;
  end: number;
  page: number;
  pages: number;
  setPage: (page: number) => void;
} {
  const [areaRef, areaHeight] = useHeight<HTMLDivElement>();
  const [row, setRow] = useState(0);
  const [head, setHead] = useState(0);
  const [page, setPage] = useState(0);
  const rowRef = useCallback((element: HTMLElement | null) => {
    if (element !== null && element.offsetHeight > 0) setRow(element.offsetHeight);
  }, []);
  const headRef = useCallback((element: HTMLElement | null) => {
    if (element !== null) setHead(element.offsetHeight);
  }, []);

  useEffect(() => {
    setPage(0);
  }, [resetOn]);

  const per =
    areaHeight <= 0
      ? Math.max(1, count)
      : Math.max(1, Math.floor((areaHeight - head) / (row > 0 ? row : estimate)));
  const pages = Math.max(1, Math.ceil(count / per));
  const current = Math.min(page, pages - 1);
  const start = current * per;
  return {
    areaRef,
    rowRef,
    headRef,
    start,
    end: Math.min(count, start + per),
    page: current,
    pages,
    setPage,
  };
}

/** The class for a box `useRowPages` measures: takes the room left, never scrolls. */
export const fitArea = styles.fitArea;

/**
 * `useFitPages` for a table: rows of any height, measured, packed under the
 * table's own head. Put `areaRef` on the box around the table, `headRef` on its
 * `<thead>` and `measure(key)` on each `<tr>`.
 */
export function useTablePages({
  keys,
  estimate,
  resetOn,
}: {
  keys: readonly string[];
  estimate: (index: number) => number;
  resetOn?: unknown;
}): FitPages & { headRef: (element: HTMLElement | null) => void } {
  const [areaRef, areaHeight] = useHeight<HTMLDivElement>();
  const [head, setHead] = useState(0);
  const headRef = useCallback((element: HTMLElement | null) => {
    if (element !== null) setHead(element.offsetHeight);
  }, []);
  const fit = useFitPages({
    keys,
    estimate,
    gap: 0,
    resetOn,
    budget: areaHeight > 0 ? Math.max(1, areaHeight - head) : 0,
  });
  return { ...fit, areaRef, headRef };
}

/**
 * **How tall a part of a fitted report may be**, in px — set by Accounting's
 * `FitSections` around each tab. A `PagedRows` inside it pages to that room
 * rather than to a fixed count, so a long table never runs past the window.
 * Null everywhere else.
 */
export const FitRoom = createContext<number | null>(null);

/**
 * A table inside a report that is itself taller than the screen (Accounting):
 * there is no box to fit, so it shows a fixed number of rows and pages the rest
 * (2026-10-16, on a busy clinic — a day's bookings were seventy rows long).
 */
export function PagedRows<T>({
  rows,
  per = 12,
  noun,
  testId,
  children,
}: {
  rows: readonly T[];
  /** Rows a page, where no fitted room is given. */
  per?: number;
  noun: string;
  testId?: string;
  children: (shown: readonly T[], offset: number) => React.ReactNode;
}): React.JSX.Element {
  const room = useContext(FitRoom);
  const marker = useRef<HTMLSpanElement | null>(null);
  const [rowPx, setRowPx] = useState(0);
  const [chromePx, setChromePx] = useState(0);
  const [page, setPage] = useState(0);

  /**
   * **Inside a fitted report, a page is what the room holds** (Emile,
   * 2026-10-17: fit every screen size). The part this table sits in is
   * measured as drawn: one row's height, and everything around the rows (the
   * card's head, the table's head, this pager), so the rows get the rest.
   */
  const fitted =
    room !== null && room > 0 && rowPx > 0
      ? Math.max(3, Math.floor((room - chromePx) / rowPx))
      : per;
  const pages = Math.max(1, Math.ceil(rows.length / fitted));
  const current = Math.min(page, pages - 1);
  const start = current * fitted;
  const shown = rows.slice(start, start + fitted);

  useLayoutEffect(() => {
    if (room === null) return;
    const anchor = marker.current;
    const host = anchor?.parentElement ?? null;
    if (host === null) return;
    const first = host.querySelector<HTMLElement>("tbody tr, [data-card], [data-row]");
    const height = first?.offsetHeight ?? 0;
    if (height > 0 && Math.abs(height - rowPx) > 1) setRowPx(height);
    const part = (host.closest("section") as HTMLElement | null) ?? host;
    const chrome = Math.ceil(part.getBoundingClientRect().height - shown.length * height);
    if (height > 0 && Math.abs(chrome - chromePx) > 2) setChromePx(chrome);
  });

  return (
    <>
      <span ref={marker} hidden />
      {children(shown, start)}
      <Pager
        page={current}
        count={pages}
        onPage={setPage}
        from={start + 1}
        to={start + shown.length}
        total={rows.length}
        noun={noun}
        testId={testId}
      />
    </>
  );
}
